// TL-6 Circle: center → radius point, Measurements `radius`.
import { dist, type Vec } from "../core/geometry";
import { formatLength, parseScalar } from "../core/units";
import { newId } from "../doc/document";
import type { CircleEntity } from "../doc/entities";
import type { RenderView } from "../engine/renderEntities";
import { strokeWidthWorld } from "../engine/renderEntities";
import { drawReadout } from "./line";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

export class CircleTool implements Tool {
  readonly id = "circle" as const;
  private center: Vec | null = null;
  private cursor: Vec | null = null;

  constructor(private ctx: ToolCtx) {}

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset();
  }

  inProgress(): boolean {
    return this.center !== null;
  }

  private reset(): void {
    this.center = null;
    this.cursor = null;
    this.ctx.setSnapBase(null);
    this.ctx.setMeasure("radius", "radius");
    this.ctx.setHint("Circle: click the center · Esc = cancel");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (!this.center) {
      this.center = e.point;
      this.ctx.setSnapBase(e.point);
      this.ctx.setHint("Circle: click a point on the circle — type a radius ⏎");
      this.ctx.setCursorTip("edge point · radius ⏎");
      this.ctx.requestRender();
      return;
    }
    this.commit(dist(e.point, this.center));
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.center) {
      this.reset();
      return true;
    }
    return false;
  }

  measurement(raw: string): boolean {
    if (!this.center) return false;
    const { units } = this.ctx.docUnits();
    const r = parseScalar(raw, units);
    if (r === null || r <= 1e-9) return false; // EC-DM-6
    this.commit(r);
    return true;
  }

  private commit(r: number): void {
    if (r <= 1e-9 || !this.center) return; // INV-5 guard: ignore the click, keep waiting
    const doc = this.ctx.store.doc;
    const entity: CircleEntity = {
      id: newId(),
      type: "circle",
      layerId: this.ctx.activeLayerId(),
      locked: false,
      hidden: false,
      center: this.center,
      r,
      style: structuredClone(doc.defaultStyle)
    };
    this.ctx.store.dispatch({
      kind: "addEntities",
      entities: [entity],
      z: [{ layerId: entity.layerId, index: -1 }]
    });
    const entityId = entity.id;
    const frozenR = r;
    this.ctx.registerReapply((rawValue) => {
      const { units } = this.ctx.docUnits();
      const nr = parseScalar(rawValue, units);
      if (nr === null || nr <= 1e-9) return false;
      this.ctx.store.dispatch({
        kind: "updateEntities",
        patches: [{ id: entityId, before: { r: frozenR }, after: { r: nr } }]
      });
      return true;
    });
    this.reset();
  }

  renderDynamic(v: RenderView): void {
    if (!this.center || !this.cursor) return;
    const r = dist(this.cursor, this.center);
    if (r < 1e-9) return;
    const doc = this.ctx.store.doc;
    const { ctx } = v;
    ctx.strokeStyle = doc.defaultStyle.strokeColor;
    ctx.lineWidth = strokeWidthWorld(doc.defaultStyle, v.cam, v.dpr);
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, r, 0, Math.PI * 2);
    ctx.stroke();
    const { units, precision } = this.ctx.docUnits();
    drawReadout(v, this.cursor, `R ${formatLength(r, units, precision)}`);
  }
}
